import { Translate } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { DosageStageValidation, TimesOfDayDosageCycle } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../../../translations';
import { ExtendedDosageFormController } from '../ExtendedDosageForm.types';
import {
   createTimesOfDayDosageCycle,
   DOSAGE_TIMES_OF_DAY,
   getAdministrationIntervalDays,
} from '../../extendedDosage.utils';

import { AdministrationIntervalSelect } from './AdministrationIntervalSelect';
import { DosagePeriodField } from './DosagePeriodField';
import { StyledStagePanel, StyledTimesOfDayGrid } from './StageField.styled';
import { TimeOfDayPortionField } from './TimeOfDayPortionField';
import { createStageFieldNameFactory } from './StageFieldList.utils';
import { useTimesOfDayDurationAmountSteps } from './TimesOfDayDosageCycleField.hooks';

const DEFAULTS = createTimesOfDayDosageCycle();

type TimesOfDayDosageCycleFieldProps = Pick<ExtendedDosageFormController, 'register' | 'watch' | 'trigger'> & {
   readonly field: Partial<TimesOfDayDosageCycle>;
   readonly index: number;
   readonly validation: DosageStageValidation;
   readonly portionUnit: string | null;
};

export const TimesOfDayDosageCycleField = (props: TimesOfDayDosageCycleFieldProps): JSX.Element => {
   const createFieldName = createStageFieldNameFactory(props.index);
   const administrationIntervalDaysDefaultValue = getAdministrationIntervalDays(props.field.administrationIntervalDays);
   const durationAmountSteps = useTimesOfDayDurationAmountSteps({
      watch: props.watch,
      index: props.index,
      administrationIntervalDaysDefaultValue: administrationIntervalDaysDefaultValue,
   });

   return (
      <StyledFlex direction={'column'} gap={'8px'}>
         <StyledStagePanel>
            <StyledTimesOfDayGrid>
               {DOSAGE_TIMES_OF_DAY.map((timeOfDay, timeOfDayIndex) => (
                  <TimeOfDayPortionField
                     key={timeOfDay}
                     register={props.register}
                     watch={props.watch}
                     index={props.index}
                     timeOfDay={timeOfDay}
                     timeOfDayIndex={timeOfDayIndex}
                     defaultValue={Number(
                        props.field.portions?.find((portion) => portion.timeOfDay === timeOfDay)?.portion ?? 0
                     )}
                     portionUnit={props.portionUnit}
                  />
               ))}
            </StyledTimesOfDayGrid>
         </StyledStagePanel>
         <StyledFlex direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={'6px'}>
            <Translate>{prescriptionTranslationKeys.extendedDosage.administer}</Translate>
            <AdministrationIntervalSelect
               register={props.register}
               trigger={props.trigger}
               name={createFieldName('administrationIntervalDays')}
               defaultValue={administrationIntervalDaysDefaultValue}
               invalid={props.validation.durationNotMultipleOfIntervalAmount !== undefined}
               testId={`administrationInterval-${props.index}`}
            />
            <Translate>{prescriptionTranslationKeys.extendedDosage.during}</Translate>
            <DosagePeriodField
               register={props.register}
               watch={props.watch}
               trigger={props.trigger}
               amountName={createFieldName('duration.amount')}
               unitName={createFieldName('duration.unit')}
               amountDefaultValue={Number(props.field.duration?.amount ?? DEFAULTS.duration.amount)}
               unitDefaultValue={props.field.duration?.unit ?? DEFAULTS.duration.unit}
               unitDisabled
               amountSteps={durationAmountSteps}
               invalid={
                  props.validation.durationNotMultipleOfIntervalAmount !== undefined ||
                  props.validation.exceededPackagesCount !== undefined
               }
               testId={`duration-${props.index}`}
            />
         </StyledFlex>
      </StyledFlex>
   );
};
